// ═══════════════════════════════════════════════════════════════
// ErrorBoundary.jsx — Catches uncaught render errors.
//
// Without a boundary, any exception thrown during render makes React
// unmount the whole tree and the user sees a blank white page, with
// the error recorded nowhere. That is undiagnosable after the fact:
// the report is "blank screen" and the evidence no longer exists.
//
// This captures the error, reports it to console and analytics, and
// shows the message on screen so a user can read it back.
// ═══════════════════════════════════════════════════════════════

import React from 'react';
import { logAnalyticsEvent } from '../shared.jsx';

// Firebase Analytics rejects long string params, so keep them short.
const trim = (value, max = 100) => String(value ?? '').slice(0, max);

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null, copied: false };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });

    // Console first: this is what survives in a device log and what a
    // remote debugger will show.
    console.error('[ErrorBoundary] Uncaught render error:', error);
    console.error('[ErrorBoundary] Component stack:', info?.componentStack);
    console.error('[ErrorBoundary] Path:', window.location.pathname + window.location.search);

    logAnalyticsEvent('app_render_error', {
      error_name: trim(error?.name, 40),
      error_message: trim(error?.message),
      error_path: trim(window.location.pathname + window.location.search),
      // First stack frame is usually enough to place the throw.
      error_frame: trim((info?.componentStack || '').trim().split('\n')[0]),
    });
  }

  handleCopy = () => {
    const { error, info } = this.state;
    const text = [
      'The ArguMentor error report',
      'When: ' + new Date().toISOString(),
      'Page: ' + window.location.href,
      'Agent: ' + navigator.userAgent,
      'Error: ' + (error?.name || 'Error') + ': ' + (error?.message || String(error)),
      'Stack: ' + (error?.stack || '(none)'),
      'Components: ' + (info?.componentStack || '(none)'),
    ].join('\n');
    try {
      navigator.clipboard.writeText(text);
      this.setState({ copied: true });
    } catch (e) {
      this.setState({ copied: false });
    }
  };

  render() {
    const { error, info, copied } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-stone-100 flex items-center justify-center px-4 py-12">
        <div className="max-w-lg w-full bg-white rounded-2xl shadow-xl border border-stone-200 p-6">
          <h1 className="text-lg font-semibold text-stone-800 mb-2">Something went wrong</h1>
          <p className="text-sm text-stone-600 mb-4">
            The app hit an error it could not recover from. Reloading usually fixes it. If it keeps
            happening, send us the details below and we will chase it down.
          </p>

          <div className="bg-stone-50 border border-stone-200 rounded-lg p-3 mb-4">
            <p className="text-xs font-mono text-stone-700 break-words">
              {(error?.name || 'Error') + ': ' + (error?.message || String(error))}
            </p>
            {info?.componentStack && (
              <p className="text-[11px] font-mono text-stone-500 mt-2 break-words">
                {info.componentStack.trim().split('\n').slice(0, 3).join('\n')}
              </p>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-stone-900 text-sm font-medium rounded-lg transition-colors"
            >
              Reload
            </button>
            <button
              onClick={this.handleCopy}
              className="px-4 py-2 bg-stone-100 hover:bg-stone-200 text-stone-700 text-sm font-medium rounded-lg transition-colors"
            >
              {copied ? 'Copied' : 'Copy details'}
            </button>
            <a
              href="/"
              className="px-4 py-2 text-stone-600 hover:text-stone-800 text-sm font-medium transition-colors"
            >
              Start over
            </a>
          </div>

          <p className="text-xs text-stone-500 mt-4">
            Need help? Contact <a href="mailto:info@4hci.com" className="text-amber-700 hover:underline">info@4hci.com</a>
          </p>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
